import PurchaseItem from "./purchaseItem";
import EDocument from "./edocument";
import store from '../store'
import Big from "big.js";
import { computeTaxTotals } from "./taxCatalog";
import { retentionRate } from "./purchasePayload";

// Cota del campo de observaciones. Espeja `Purchase.observations` del backend
// (backend/models.py).
export const OBSERVATIONS_MAX_LENGTH = 400;

const Purchase = class {
    constructor(object) {
        this.rentPercentageOptions = [
            {name: '0%', value: '0.0'}, {name: '1%', value: '0.01'}, {name: '1.75%', value: '0.0175'},
            {name: '2%', value: '0.02'}, {name: '2.75%', value: '0.0275'}, {name: '8%', value: '0.08'}, {name: '10%', value: '0.1'},
        ]

        this.ivaPercentageOptions = [
            {name: '0%', value: '0.0'},
            {name: '10%', value: '0.1'}, {name: '20%', value: '0.2'}, {name: '30%', value: '0.3'},
            {name: '50%', value: '0.5'}, {name: '70%', value: '0.7'}, {name: '100%', value: '1'},
        ]
        
        if (object === undefined) {
            this.id = null;
            this.vendorName = null;
            this.vendorIdentification = null;
            this.reason = null;
            this.serialNumber = null;
            this.status = null;
            this.sub0 = new Big('0.0');
            this.sub12 = new Big('0.0');
            // Desglose de la base gravada para la ENTRADA MANUAL: sub5 es la
            // porción al 5% y sub15 la de tarifa general. Siempre sub12 = sub5 + sub15.
            this.sub5 = new Big('0.0');
            this.sub15 = new Big('0.0');
            // ICE que cobró el proveedor: se registra tal cual, no se recalcula.
            this.iceValue = new Big('0.0');
            // IRBPNR (botellas plásticas) que cobró el proveedor: sale del XML y, como el
            // ICE, va al costo de los productos enlazados a sus líneas.
            this.irbpnrValue = new Big('0.0');
            this.tax = new Big('0.0');
            this.taxPercentage = store.getters.business.taxPercentage;
            this.discount = new Big('0.0');
            this.total = new Big('0.0');
            this.createdAt = new Date();
            this.creditDays = 0;
            this.paymentMethod = {name: 'Efectivo', code: 'CASH'};
            this.items = [];
            this.ivaPercentage = null;
            this.rentPercentage = null;
            this.edoc = null;
            this.providerEmail = undefined;
            this.edocStatus = null;
            this.observations = null;
            this.accessCode = null;
            this.receiptXML = null;
            this.expenseType = null;
            this.basicExpenseType = null;
            this.paidAmount = new Big('0.0'); // Σ abonos registrados
            this.pendingAmount = null; // Saldo por pagar (null = sin dato del backend)
            this.payments = []; // Abonos registrados (serializados)

        } else {
            this.id = object.id;
            this.vendorName = object.vendorName || null;
            this.vendorIdentification = object.vendorIdentification || null;
            this.reason = object.reason || null;
            this.serialNumber = object.serialNumber || null;
            this.status = object.status || null;
            this.sub0 = (object.base0 !== undefined) ? new Big(object.base0) : new Big('0.0');
            this.sub12 = (object.base12 !== undefined) ? new Big(object.base12) : new Big('0.0');
            this.iceValue = (object.iceValue !== undefined && object.iceValue !== null) ? new Big(object.iceValue) : new Big('0.0');
            this.irbpnrValue = (object.irbpnrValue !== undefined && object.irbpnrValue !== null) ? new Big(object.irbpnrValue) : new Big('0.0');
            // Reconstruir el desglose desde las taxLines guardadas. Sin ellas
            // (compras legacy) se asume toda la base gravada a tarifa general.
            this.sub5 = new Big('0.0');
            this.sub15 = this.sub12;
            if (Array.isArray(object.taxLines) && object.taxLines.length > 0) {
                let five = new Big('0.0'), rest = new Big('0.0');
                object.taxLines.forEach(function (line) {
                    if (line.codigo === '3') { return; }          // ICE, no es tarifa de IVA
                    const rate = new Big(line.tarifa || '0');
                    if (rate.eq(0)) { return; }                    // 0% ya está en sub0
                    if (rate.eq(5)) { five = five.plus(line.baseImponible); }
                    else { rest = rest.plus(line.baseImponible); }
                });
                this.sub5 = five;
                this.sub15 = rest;
            }
            this.tax = (object.tax !== undefined) ? new Big(object.tax) : new Big('0.0');
            this.taxPercentage = object.taxPercentage || store.getters.business.taxPercentage;
            this.discount = (object.discount !== undefined) ? new Big(object.discount) : new Big('0.0');
            this.total = (object.total !== undefined) ? new Big(object.total) : new Big('0.0');
            this.createdAt = new Date(object.createdAt);
            this.creditDays = object.creditDays;
            this.paymentMethod = object.paymentMethod;
            this.edocStatus = object.edocStatus;
            this.observations = object.observations || null;
            this.accessCode = object.accessCode || null;
            this.receiptXML = object.receiptXML || null;
            this.expenseType = object.expenseType || null;
            this.basicExpenseType = object.basicExpenseType || null;
            this.paidAmount = (object.paidAmount !== undefined) ? new Big(object.paidAmount) : new Big('0.0');
            this.pendingAmount = (object.pendingAmount !== undefined) ? new Big(object.pendingAmount) : null;
            this.payments = (object.payments !== undefined && object.payments !== null) ? object.payments : [];

            this.rentPercentage = this.getRentPercentageOption(object.rentRetentionPercentage);
            this.ivaPercentage = this.getIVAPercentageOption(object.ivaRetentionPercentage);

            // Parse edoc
            if (object.edoc !== undefined && object.edoc !== null) {
                this.edoc = new EDocument(object.edoc);
            } else { this.edoc = null }
            // Correo del proveedor para la retención (H48): null = no tiene; undefined = no vino.
            this.providerEmail = object.providerEmail;

            // Parse items if exists
            if (object.items !== undefined && object.items !== null && object.items.length > 0) { 
                this.items = object.items.map(item => new PurchaseItem(item)); 
            } else { this.items = [] }
        }
    }

    get title() {
        return this.vendorName ? this.vendorName : this.reason
    }
    
    get serial() {
        return this.serialNumber ? this.serialNumber : this.id.toString().padStart(9, "0")
    }

    get subtotal() {
        const BigJS = require('big.js');
        const sub0 = this.sub0 instanceof BigJS ? this.sub0 : new Big(this.sub0 || 0);
        const sub12 = this.sub12 instanceof BigJS ? this.sub12 : new Big(this.sub12 || 0);
        return sub0.plus(sub12).round(2);
    }

    get createdAtFormatted() {
        var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
        return this.createdAt.toLocaleDateString('es-EC', options);
    }

    get isNulled() {
        return this.edoc !== null && this.edoc.nulled === true
    }

    get dueDate() {
        if (this.creditDays > 0) {
            var dueDate = (this.createdAt !== null) ? new Date(this.createdAt) : new Date();
            dueDate.setDate(dueDate.getDate() + parseInt(this.creditDays));
            return dueDate;
        }
        return null;
    }
    get dueDateFormatted() {
        if (this.dueDate !== null) {
            var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
            return this.dueDate.toLocaleDateString('es-EC', options);
        }
        return null;
    }
    get hasPartialPayments() {
        // Compra por pagar con al menos un abono registrado
        return this.status === "PENDING" && this.paidAmount !== undefined
            && this.paidAmount !== null && Number(this.paidAmount.toString()) > 0;
    }
    get collectStatusReadable() {
        if (this.status === "PAID") { return "PAGADA"}
        if (this.status === "PENDING" && this.dueDate !== null && this.dueDate <= new Date()) { return "VENCIDA"}
        if (this.hasPartialPayments) { return "ABONADA" }
        else { return "POR PAGAR" }
    }
    get collectStatusBadgeColor() {
        if (this.status === "PAID") { return "green"}
        if (this.status === "PENDING" && this.dueDate !== null && this.dueDate <= new Date()) { return "orange"}
        if (this.hasPartialPayments) { return "yellow" }
        else { return "blue" }
    }

    get paymentMethodReadable() {
        if (this.paymentMethod === "CASH") { return "Efectivo"}
        if (this.paymentMethod === "TRANSFER") { return "Transferencia"}
        if (this.paymentMethod === "DEBIT_CARD") { return "Tarj. Débito"}
        if (this.paymentMethod === "CREDIT_CARD") { return "Tarj. Crédito"}
        if (this.paymentMethod === "MIXED") { return "Mixto"}
        else { return "Cheque" }
    }
    get paymentMethodBadgeColor() {
        if (this.paymentMethod === "CASH") { return "green"}
        if (this.paymentMethod === "TRANSFER") { return "blue"}
        else { return "gray" }
    }

    // Aviso cuando la retención autorizada no se pudo enviar por correo (H48).
    get retentionEmailNotice() {
        if (!this.edoc || this.edoc.authorized !== true || this.edoc.nulled === true) return null;
        if (this.providerEmail !== null) return null;
        return 'El proveedor no tiene correo: la retención no se envió por correo.';
    }

    get edocState() {
        if (this.edocStatus === "none") { return "NO APLICA" }
        else if (this.edocStatus === "nulled") { return "ANULADA" }
        // Con solicitud en curso el comprobante SIGUE VIGENTE: se distingue de
        // ANULADA a proposito, porque en retenciones el receptor tiene 5 dias
        // habiles para aceptar y puede no aceptar nunca.
        else if (this.edocStatus === "annulmentSending") { return "ANULANDO" }
        else if (this.edocStatus === "annulmentPending") { return "ANULACIÓN SOLICITADA" }
        else if (this.edocStatus === "authorized") { return "AUTORIZADO" }
        else if (this.edocStatus === "received") { return "POR AUTORIZAR" }
        else { return "POR REPORTAR" }
    }
    get edocStateBadgeColor() {
        if (this.editable === true) { return "blue" }
        else if (this.edocStatus === "none") { return "gray" }
        else if (this.edocStatus === "nulled") { return "red" }
        // Naranja, NO rojo: no esta anulada, esta esperando.
        else if (this.edocStatus === "annulmentSending") { return "orange" }
        else if (this.edocStatus === "annulmentPending") { return "orange" }
        else if (this.edocStatus === "authorized") { return "green" }
        else if (this.edocStatus === "received") { return "yellow" }
        else { return "orange" }
    }

    // retentionRate tolera la opción del desplegable, un número o nada: antes un
    // porcentaje ya convertido a número hacía `new Big(undefined)` y tumbaba el render (H26).
    get rentRetentionValue() {
        return retentionRate(this.rentPercentage).times(this.sub0.plus(this.sub12)).round(2);
    }

    get ivaRetentionValue() {
        return retentionRate(this.ivaPercentage).times(this.tax).round(2);
    }

    get netPayable() {
        // Valor neto a pagar = Total - Retención RENTA - Retención IVA
        return this.total.minus(this.rentRetentionValue).minus(this.ivaRetentionValue).round(2);
    }

    get hasRetention() {
        return !retentionRate(this.rentPercentage).eq(0) || !retentionRate(this.ivaPercentage).eq(0);
    }

    get shouldCreateEDOC() {
        return !(this.id === null) &&
                !(this.vendorName === "" || this.vendorName === null) &&
                !(this.vendorIdentification === "" || this.vendorIdentification === null) &&
                !(this.rentPercentage === "" || this.rentPercentage === null) &&
                !(this.ivaPercentage === "" || this.ivaPercentage === null)
    }

    get reportedWithReceipt() {
        return (this.id !== null) && 
                (this.vendorName !== "" && this.vendorName !== null) && 
                (this.vendorIdentification !== "" && this.vendorIdentification !== null) &&
                (this.serialNumber !== null && this.serialNumber !== undefined)
    }

    get hasXMLLoaded() {
        // Indica si hay un XML cargado (receiptXMLUrl o receiptXML presente)
        return (this.receiptXMLUrl !== null && this.receiptXMLUrl !== undefined) ||
               (this.receiptXML !== null && this.receiptXML !== undefined);
    }

    addItem(item) {
        let index = this.getItemIndex(item);
        if (index === -1) { this.items.unshift(item) } 
        else { this.items[index] = item }
        this.updateValues();
     }
     deleteItem(item) {
        let index = this.getItemIndex(item);
        this.items.splice(index, 1);
        if (this.items.length == 0) { this.items = [] }
        this.updateValues();
    }
    getItemIndex(item) {
        if (this.items === null || this.items === undefined) { return -1; }
        // Un ítem enlazado se identifica por su línea del comprobante: dos líneas pueden
        // ser el mismo producto, y una línea va a un solo producto.
        const line = (item.receiptLine !== undefined) ? item.receiptLine : null;
        if (line !== null) {
            return this.items.findIndex(other => other.receiptLine === line);
        }
        if (item.productId === null) { return -1; }
        return this.items.findIndex(other => (other.receiptLine === null || other.receiptLine === undefined) &&
                                             other.productId === item.productId);
    }

    updateValues() {
        // Si hay XML cargado, mantener los valores del XML y no recalcular
        if (this.hasXMLLoaded) {
            return;
        }
        
        var discount_ = new Big('0.0');
        var taxLines = [];
        for (var i=0; i<this.items.length; i++) {
            var base = this.items[i].unitPrice.times(this.items[i].amount).times(this.items[i].discount.times(-1).plus(1));
            taxLines.push({ ivaCode: this.items[i].ivaCode, base: base });
            discount_ = discount_.plus(this.items[i].unitPrice.times(this.items[i].amount).times(this.items[i].discount));
        }
        // IVA agregado POR TARIFA (ver computeTaxTotals, espejo del backend):
        // una compra puede mezclar 5% y 15%.
        var totals = computeTaxTotals(taxLines);
        this.sub0 = totals.sub0;
        this.sub12 = totals.sub12;
        this.tax = totals.tax;
        // Mantener el desglose 5% / general en sync con los buckets, para que
        // la caja de totales muestre lo mismo venga de items o de entrada manual.
        this.sub5 = new Big('0.0');
        this.sub15 = new Big('0.0');
        var self = this;
        totals.buckets.forEach(function (b) {
            if (b.rate.eq(0)) { return; }
            if (b.rate.eq(5)) { self.sub5 = self.sub5.plus(b.base); }
            else { self.sub15 = self.sub15.plus(b.base); }
        });
        this.discount = discount_.round(2);
        this.total = this.sub0.plus(this.sub12).plus(this.tax).plus(this.iceValue || new Big('0.0')).round(2);
    }
    getRentPercentageOption(percentage) {
        for (var i=0; i<this.rentPercentageOptions.length; i++) {
            if (percentage==this.rentPercentageOptions[i].value) {
                return this.rentPercentageOptions[i];
            } 
        }
        return null;
    }
    getIVAPercentageOption(percentage) {
        for (var i=0; i<this.ivaPercentageOptions.length; i++) {
            if (percentage==this.ivaPercentageOptions[i].value) {
                return this.ivaPercentageOptions[i]; 
            } 
        }
        return null;
    }
}

export default Purchase